/* eslint-disable max-len */
import { Formik, useFormikContext } from 'formik';
import { ReactElement, useEffect, useMemo } from 'react';
import { PanelType } from '../../../types/PanelType';
import {
  getLiteracyScreeningDatePickerBounds,
  getLiteracyScreeningNotificationDatesValidationSchema,
} from '../../../types/Validation/LiteracyScreeningScreenerValidationSchema';
import FormDateSelector from '../../FormInputs/FormDateSelector/FormDateSelector';
import Panel from '../../Panel/Panel';

export interface LiteracyScreeningNotificationDates {
  beginningOfYearNotificationDate: string;
  middleOfYearNotificationDate: string;
  endOfYearNotificationDate: string;
}

export interface LiteracyScreeningNotificationDatesEditState {
  dirty: boolean;
  hasError: boolean;
  values: LiteracyScreeningNotificationDates;
}

interface LiteracyScreeningParentNotificationDatesPanelProps {
  schoolYear: string;
  values: LiteracyScreeningNotificationDates;
  isBeginningOfYearGridComplete: boolean;
  isMiddleOfYearGridComplete: boolean;
  isEndOfYearGridComplete: boolean;
  disabled?: boolean;
  onEditStateChange?: (
    state: LiteracyScreeningNotificationDatesEditState
  ) => void;
}

const EMPTY_NOTIFICATION_DATES: LiteracyScreeningNotificationDates = {
  beginningOfYearNotificationDate: '',
  middleOfYearNotificationDate: '',
  endOfYearNotificationDate: '',
};

const toNotificationDates = (
  values: LiteracyScreeningNotificationDates
): LiteracyScreeningNotificationDates => ({
  beginningOfYearNotificationDate: values.beginningOfYearNotificationDate || '',
  middleOfYearNotificationDate: values.middleOfYearNotificationDate || '',
  endOfYearNotificationDate: values.endOfYearNotificationDate || '',
});

const NotificationDatesStateReporter = ({
  onEditStateChange,
  validationSchema,
}: {
  onEditStateChange?: (
    state: LiteracyScreeningNotificationDatesEditState
  ) => void;
  validationSchema: ReturnType<
    typeof getLiteracyScreeningNotificationDatesValidationSchema
  >;
}): null => {
  const { values, dirty } =
    useFormikContext<LiteracyScreeningNotificationDates>();

  useEffect(() => {
    onEditStateChange?.({
      dirty,
      hasError: !validationSchema.isValidSync(values),
      values: toNotificationDates(values),
    });
  }, [dirty, onEditStateChange, validationSchema, values]);

  return null;
};

const LiteracyScreeningParentNotificationDatesPanel = ({
  schoolYear,
  values,
  isBeginningOfYearGridComplete,
  isMiddleOfYearGridComplete,
  isEndOfYearGridComplete,
  disabled = false,
  onEditStateChange,
}: LiteracyScreeningParentNotificationDatesPanelProps): ReactElement => {
  const validationSchema = useMemo(
    () =>
      getLiteracyScreeningNotificationDatesValidationSchema(schoolYear, {
        isBeginningOfYearGridComplete,
        isMiddleOfYearGridComplete,
        isEndOfYearGridComplete,
      }),
    [
      isBeginningOfYearGridComplete,
      isEndOfYearGridComplete,
      isMiddleOfYearGridComplete,
      schoolYear,
    ]
  );
  const datePickerBounds = useMemo(
    () => getLiteracyScreeningDatePickerBounds(schoolYear),
    [schoolYear]
  );
  const initialValues = useMemo(
    () => toNotificationDates(values || EMPTY_NOTIFICATION_DATES),
    [values]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={() => undefined}
    >
      <>
        <NotificationDatesStateReporter
          onEditStateChange={onEditStateChange}
          validationSchema={validationSchema}
        />
        <Panel
          panelType={PanelType.INFO}
          heading="Parent Notification Dates"
          className="literacy-screening-notification-dates-panel"
        >
          <div className="field-row field-row-grid-3">
            <FormDateSelector
              displayName="Beginning of Year Notification Date"
              field="beginningOfYearNotificationDate"
              validationSchema={validationSchema}
              disabled={disabled || !isBeginningOfYearGridComplete}
              classConfig={{
                labelClasses: 'forced-required-label',
              }}
              minDate={datePickerBounds.minDate}
              maxDate={datePickerBounds.maxDate}
            />
            <FormDateSelector
              displayName="Middle of Year Notification Date"
              field="middleOfYearNotificationDate"
              validationSchema={validationSchema}
              disabled={disabled || !isMiddleOfYearGridComplete}
              classConfig={{
                labelClasses: 'forced-required-label',
              }}
              minDate={datePickerBounds.minDate}
              maxDate={datePickerBounds.maxDate}
            />
            <FormDateSelector
              displayName="End of Year Notification Date"
              field="endOfYearNotificationDate"
              validationSchema={validationSchema}
              disabled={disabled || !isEndOfYearGridComplete}
              classConfig={{
                labelClasses: 'forced-required-label',
              }}
              minDate={datePickerBounds.minDate}
              maxDate={datePickerBounds.maxDate}
            />
          </div>
        </Panel>
      </>
    </Formik>
  );
};

export default LiteracyScreeningParentNotificationDatesPanel;
